/**
 * „Wieder da“ -- erst prüfen, dann buchen (Rückmeldungen 4.1).
 *
 * Die gescannten Geräte gehen an ihren Stammplatz. Vorher steht jede geplante
 * Buchung da, gruppiert nach Ziel, und lässt sich abwählen. Was abgewählt ist
 * oder nicht gebucht werden kann, bleibt als Rest in der Sammelliste: schon
 * da, ohne Stammplatz, oder mit einem Status, der liegen bleibt (ADR 0025).
 *
 * Wie bei „Alles da“ eine bewusste Ausnahme von CLAUDE.md §7: Die Übersicht
 * fragt, bevor sie bucht. Ausgelöst wird sie absichtlich, „Abbrechen“ geht
 * immer. Alles aus der lokalen Kopie, also auch offline.
 */

import { useMemo, useState } from "react";
import { bestandLaden, useBestand, type Bestand } from "../lokal/bestand.js";
import { woWar, type Gebucht } from "../lokal/verschieben.js";
import { wiederDaBuchen, wiederDaPlan } from "../lokal/wiederDa.js";
import { Farbpunkt, Knopf, Ladeanzeige, Nummer, Rueckmeldung } from "./bausteine.jsx";

export interface WiederDaEigenschaften {
  ids: readonly string[];
  aufAbbruch: () => void;
  /** Gebucht ist, was zurückging; alles andere bleibt in der Sammelliste. */
  aufGebucht: (gebucht: Gebucht[]) => void;
}

function name(bestand: Bestand, id: string): { name: string; nummer: string } {
  const a = bestand.artikel.get(id);
  return { name: a?.bezeichnung ?? "unbekannter Artikel", nummer: a?.inventarnummer ?? "" };
}

function ortName(bestand: Bestand, id: string): string {
  const o = bestand.lagerorte.get(id);
  if (o === undefined) return "unbekannten Lagerort";
  return o.standardziel ? o.bezeichnung : o.kennung;
}

/** Eine Gruppe, die nicht gebucht wird -- nur zur Kenntnis. */
function Rest({
  titel,
  ids,
  bestand,
  mitStatus = false,
}: {
  titel: string;
  ids: readonly string[];
  bestand: Bestand;
  mitStatus?: boolean;
}) {
  if (ids.length === 0) return null;
  return (
    <section
      aria-label={titel}
      className="rounded-xl border border-dashed border-neutral-400 p-3 dark:border-neutral-600"
    >
      <h3 className="mb-1 font-semibold">{titel}</h3>
      <ul className="flex flex-col">
        {ids.map((id) => {
          const { name: n, nummer } = name(bestand, id);
          const z = bestand.zustaende.get(id);
          const art = bestand.statusArten.find((s) => s.schluessel === z?.status);
          return (
            <li
              key={id}
              className="flex min-h-12 flex-col justify-center border-b border-neutral-200 py-2 last:border-b-0 dark:border-neutral-700"
            >
              <span className="flex items-center gap-2 font-semibold">
                {mitStatus && art !== undefined && <Farbpunkt farbe={art.farbe} />}
                {n}
              </span>
              <span className="flex flex-wrap gap-x-3 text-sm text-neutral-600 dark:text-neutral-300">
                <Nummer>{nummer}</Nummer>
                {mitStatus && <span>{art?.bezeichnung ?? z?.status}</span>}
                {z !== undefined && <span>liegt {woWar(bestand, z)}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function WiederDa({ ids, aufAbbruch, aufGebucht }: WiederDaEigenschaften) {
  const bestand = useBestand();
  const [abgewaehlt, setAbgewaehlt] = useState<ReadonlySet<string>>(new Set());
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const plan = useMemo(
    () => (bestand === undefined ? undefined : wiederDaPlan(bestand, ids)),
    [bestand, ids],
  );
  const gruppen = useMemo(() => {
    const nachZiel = new Map<string, string[]>();
    for (const z of plan?.zeilen ?? []) {
      const liste = nachZiel.get(z.stammplatz_id);
      if (liste === undefined) nachZiel.set(z.stammplatz_id, [z.artikel_id]);
      else liste.push(z.artikel_id);
    }
    return [...nachZiel];
  }, [plan]);

  const gewaehlt = new Set(
    (plan?.zeilen ?? []).map((z) => z.artikel_id).filter((id) => !abgewaehlt.has(id)),
  );

  const buchen = async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      // Frisch gelesen: Seit dem Öffnen kann sich etwas bewegt haben.
      const frisch = await bestandLaden();
      aufGebucht(await wiederDaBuchen(frisch, wiederDaPlan(frisch, ids), gewaehlt));
    } catch (f) {
      setFehler(f instanceof Error ? f.message : String(f));
      setLaeuft(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Wieder da"
      className="fixed inset-0 z-30 overflow-y-auto bg-neutral-100 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-blue-800 px-4 py-2 text-white">
        <h2 className="text-lg font-bold">Wieder da – an den Stammplatz</h2>
        <button
          type="button"
          onClick={aufAbbruch}
          className="min-h-12 rounded-lg px-3 font-semibold underline active:bg-blue-900"
        >
          Abbrechen
        </button>
      </header>

      {bestand === undefined || plan === undefined ? (
        <Ladeanzeige was="Die Übersicht" />
      ) : (
        <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 pb-28">
          {fehler !== null && <Rueckmeldung art="warnung" kopf="Nicht gebucht" zusatz={fehler} />}
          <p className="text-neutral-700 dark:text-neutral-200">
            {plan.zeilen.length === 0
              ? "Hier ist nichts an einen Stammplatz zu buchen."
              : "Diese Buchungen werden gemacht. Was nicht stimmt, abwählen — es bleibt in der Liste."}
          </p>

          {gruppen.map(([ziel, liste]) => (
            <section
              key={ziel}
              aria-label={`zurück in ${ortName(bestand, ziel)}`}
              className="rounded-xl bg-white p-3 dark:bg-neutral-800"
            >
              <h3 className="mb-1 text-sm font-semibold text-neutral-500">
                zurück in {ortName(bestand, ziel)}
              </h3>
              <ul className="flex flex-col">
                {liste.map((id) => {
                  const { name: n, nummer } = name(bestand, id);
                  const z = bestand.zustaende.get(id);
                  const an = gewaehlt.has(id);
                  return (
                    <li key={id}>
                      <label className="flex min-h-14 cursor-pointer items-center gap-3 border-b border-neutral-200 py-2 last:border-b-0 dark:border-neutral-700">
                        <input
                          type="checkbox"
                          className="size-7 shrink-0 accent-blue-700"
                          checked={an}
                          aria-label={`${n} · ${nummer}`}
                          onChange={() => {
                            setAbgewaehlt((alt) => {
                              const neu = new Set(alt);
                              if (neu.has(id)) neu.delete(id);
                              else neu.add(id);
                              return neu;
                            });
                          }}
                        />
                        <span className={`flex min-w-0 flex-col ${an ? "" : "opacity-60"}`}>
                          <span className="font-semibold">{n}</span>
                          <span className="flex flex-wrap gap-x-3 text-sm text-neutral-600 dark:text-neutral-300">
                            <Nummer>{nummer}</Nummer>
                            <span>
                              von: {z === undefined ? "noch nicht erfasst" : woWar(bestand, z)}
                            </span>
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          <Rest
            titel="Bleibt liegen – wegen des Status"
            ids={plan.liegenLassen}
            bestand={bestand}
            mitStatus
          />
          <Rest titel="Schon am Stammplatz" ids={plan.schonDa} bestand={bestand} />
          <Rest titel="Ohne Stammplatz" ids={plan.ohneStammplatz} bestand={bestand} />
        </div>
      )}

      {plan !== undefined && plan.zeilen.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-10 flex justify-center border-t border-neutral-300 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-900">
          <div className="flex w-full max-w-2xl flex-col">
            <Knopf
              art="haupt"
              disabled={gewaehlt.size === 0 || laeuft}
              onClick={() => void buchen()}
            >
              {gewaehlt.size === 0 ? "Nichts gewählt" : `${String(gewaehlt.size)} zurückbuchen`}
            </Knopf>
          </div>
        </div>
      )}
    </div>
  );
}
